import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { renameRoute, updateConnection } from 'api/routes/routesApi';
import { Organization, Provider } from 'components/common/ObjectTypes';
import RequiredTag from 'components/common/RequiredTag';
import SelectInput from 'components/common/SelectInput';
import {
  buttonClasses,
  inputClasses,
} from 'components/common/tailwindClassnames';
import { routeDisplayName } from '../routeDisplayName';
import type { RouteRecord } from './useRouteRecord';

type RouteGeneralSettingsProps = {
  record: RouteRecord;
  organizations: Organization[];
  onSearchOrgs: (search: string) => void;
  onLoadMoreOrgs: () => void;
};

// The Route's own settings above the flow map: its name and workspace.
const RouteGeneralSettings = ({
  record,
  organizations,
  onSearchOrgs,
  onLoadMoreOrgs,
}: RouteGeneralSettingsProps) => {
  const { t } = useTranslation();
  const {
    getToken,
    route,
    routeName,
    setRouteName,
    routeProvider,
    setRouteProvider,
    defaultRouteId,
    changesMade,
    setChangesMade,
    requestRouteReload,
  } = record;
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The Route's name is its own field on the route record — editing it must
  // not touch the provider Connection's name.
  const handleChangeRouteName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRouteName(e.target.value);
    setChangesMade(true);
  };

  const handleChangeRouteOwner = (e: any) => {
    setRouteProvider((prevProvider: Provider) => ({
      ...prevProvider,
      owner: { id: e.value, name: e.label, description: '' },
    }));
    setChangesMade(true);
  };

  const formIsValid = () => {
    const formErrors: Record<string, string> = {};
    if (routeName.trim().length === 0)
      formErrors.provider_name = t('Name is required.');
    if (!(routeProvider.owner?.name ?? route.owner.name))
      formErrors.provider_owner = t('Select a workspace.');

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleUpdateRoute = async () => {
    // The name goes to the route record; everything else still lives on the
    // provider integration. Renaming the Route never renames the Connection.
    const updatedProvider = {
      owner: routeProvider.owner?.id ?? route.owner.id,
      type: routeProvider.type.id,
      base_url: routeProvider.base_url,
      enabled: routeProvider.enabled,
    };
    // Keep changesMade set: the draft is still unsaved, and the Save/Cancel
    // row is what lets the user fix or abandon it.
    if (!formIsValid()) return;
    const token = getToken();
    if (token) {
      try {
        if (defaultRouteId && routeName.trim() !== routeDisplayName(route)) {
          await renameRoute(token, defaultRouteId, routeName.trim());
        }
        // Same as above: nothing back means nothing saved.
        const saved = await updateConnection(
          token,
          routeProvider.id,
          updatedProvider,
        );
        if (!saved) return;
        toast.success(`${t('Route updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        // No navigation: the name lives in a bar above the flow map, and
        // navigating to the bare subpage dropped the configurations/*
        // selection, closing whichever panel the user had open.
        setChangesMade(false);
      } catch (error: any) {
        console.log(error);
        // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
        if (error?.response?.status === 401) return;
        toast.error(
          `${t('Something went wrong. Please try again or contact support.')}`,
          {
            position: toast.POSITION.TOP_RIGHT,
          },
        );
      }
    }
  };

  const handleCancel = () => {
    setRouteName(routeDisplayName(route));
    setErrors({});
    requestRouteReload();
    setChangesMade(false);
  };

  return (
    <div className="mt-8 px-0 md:px-4">
      <div className="rounded-lg border border-teal-t30 bg-white px-5 py-4">
        {/* Two columns per row: what the field is on the left, the field
            itself on the right — same layout as the account panels below. */}
        <div className="mb-2 flex w-full flex-col gap-2 border-b border-ui-divider-lines pb-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label htmlFor="route_name" className="mr-1">
              {t('Route Name')}
              <RequiredTag size="sm" />
            </label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('A descriptive name to identify this Route.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <input
              id="route_name"
              data-testid="ManageRouteDetails-route-name-input"
              value={routeName}
              onChange={handleChangeRouteName}
              className={`${inputClasses.standard} w-full`}
            />
            {errors.provider_name && (
              <p className="mt-1 text-sm text-unlisted-orange-600">
                {errors.provider_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label htmlFor="route_workspace" className="mr-1">
              {t('Workspace')}
              <RequiredTag size="sm" />
            </label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('The workspace this Route belongs to.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <SelectInput
              inputId="route_workspace"
              options={organizations.map((org: Organization) => ({
                label: org.name,
                value: org.id,
              }))}
              className={'basic-single'}
              value={{
                label: routeProvider?.owner?.name ?? route?.owner?.name ?? '',
                value: routeProvider?.owner?.id ?? route?.owner?.id ?? '',
              }}
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isSearchable={true}
              isMulti={false}
              name={t('Workspace')}
              onChange={handleChangeRouteOwner}
              onMenuScrollToBottom={onLoadMoreOrgs}
              handleInputChange={onSearchOrgs}
            />
            {errors.provider_owner && (
              <p className="mt-1 text-sm text-unlisted-orange-600">
                {errors.provider_owner}
              </p>
            )}
          </div>
        </div>
        {/* Also while an error is showing: hiding the row would strand an
            invalid draft with no way to save the correction or cancel out
            of it. */}
        {(changesMade || Object.keys(errors).length > 0) && (
          <div className="mt-4 flex items-center gap-3">
            <button
              className={buttonClasses.primaryAction}
              onClick={handleUpdateRoute}
              data-testid="ManageRouteDetails-save-general-button"
            >
              {t('Save')}
            </button>
            <button
              className={buttonClasses.cancelAction}
              onClick={handleCancel}
            >
              {t('Cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteGeneralSettings;
